import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiPhone, FiCalendar, FiGlobe, FiDownload, FiSave, FiEdit, FiLock } = FiIcons;

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    name: 'Amina Hassan',
    email: 'amina.hassan@email.com',
    phone: '+1 (514) 555-0123',
    location: 'Laval, QC',
    bio: 'Professional home cleaner with 5+ years of experience. Reliable, thorough, and trustworthy.',
    avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=200&h=200&fit=crop&crop=face'
  });

  const [availability, setAvailability] = useState({
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '10:00', end: '16:00' },
    sunday: { enabled: false, start: '10:00', end: '16:00' }
  });

  const [preferences, setPreferences] = useState({
    language: 'en',
    notifications: {
      email: true,
      sms: true,
      push: true
    },
    privacy: {
      showPhone: true,
      showLocation: true,
      allowReviews: true
    }
  });

  const tabs = [
    { id: 'profile', label: 'Profile & Contact', icon: FiUser },
    { id: 'phone', label: 'Phone Management', icon: FiPhone },
    { id: 'availability', label: 'Availability', icon: FiCalendar },
    { id: 'preferences', label: 'Preferences', icon: FiGlobe },
    { id: 'data', label: 'Data Export', icon: FiDownload },
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'ar', name: 'العربية' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-6">
              <div className="relative">
                <img 
                  src={profileData.avatar} 
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover"
                />
                <motion.button
                  className="absolute bottom-0 right-0 bg-trust-500 text-white p-2 rounded-full hover:bg-trust-600 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <SafeIcon icon={FiEdit} className="text-sm" />
                </motion.button>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Profile Picture</h3>
                <p className="text-slate-600 text-sm">Upload a professional photo to build trust with clients</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={profileData.name}
                  onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trust-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trust-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trust-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                <input
                  type="text"
                  value={profileData.location}
                  onChange={(e) => setProfileData({...profileData, location: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trust-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Bio</label>
              <textarea
                value={profileData.bio}
                onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                rows={4}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trust-500"
                placeholder="Tell clients about your experience and what makes you special..."
              />
            </div>
          </div>
        );

      case 'phone':
        return (
          <div className="space-y-6">
            <div className="bg-trust-50 border border-trust-200 rounded-lg p-4">
              <h3 className="font-semibold text-trust-800 mb-2">Phone Number Management</h3>
              <p className="text-trust-700 text-sm">
                Your phone number is managed through our GoHighLevel integration. 
                This ensures optimal call routing and tracking.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg">
                <div>
                  <h4 className="font-medium text-slate-800">Primary Business Number</h4>
                  <p className="text-slate-600 text-sm">{profileData.phone}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                    Active
                  </span>
                  <motion.button
                    className="bg-trust-500 text-white px-4 py-2 rounded-lg hover:bg-trust-600 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Manage
                  </motion.button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-800 mb-2">Call Forwarding</h4>
                  <p className="text-slate-600 text-sm mb-3">Forward calls to your personal number</p>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Enable call forwarding</span>
                  </label>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-800 mb-2">Voicemail</h4>
                  <p className="text-slate-600 text-sm mb-3">Automatically handle missed calls</p>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Enable voicemail</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'availability':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Weekly Availability</h3>
              <div className="space-y-4">
                {Object.entries(availability).map(([day, schedule]) => (
                  <div key={day} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="w-24">
                      <span className="font-medium text-slate-800 capitalize">{day}</span>
                    </div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={schedule.enabled}
                        onChange={(e) => setAvailability({
                          ...availability,
                          [day]: { ...schedule, enabled: e.target.checked }
                        })}
                        className="rounded"
                      />
                      <span className="text-sm">Available</span>
                    </label>
                    {schedule.enabled && (
                      <div className="flex items-center gap-2 ml-4">
                        <input
                          type="time"
                          value={schedule.start}
                          onChange={(e) => setAvailability({
                            ...availability,
                            [day]: { ...schedule, start: e.target.value }
                          })}
                          className="px-3 py-1 border border-slate-200 rounded"
                        />
                        <span className="text-slate-500">to</span>
                        <input
                          type="time"
                          value={schedule.end}
                          onChange={(e) => setAvailability({
                            ...availability,
                            [day]: { ...schedule, end: e.target.value }
                          })}
                          className="px-3 py-1 border border-slate-200 rounded"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-medium text-yellow-800 mb-2">Availability Tips</h4>
              <ul className="text-yellow-700 text-sm space-y-1">
                <li>• Weekend availability can increase bookings by 25%</li>
                <li>• Evening hours (5-8 PM) are popular for tutoring services</li>
                <li>• Consider buffer time between appointments</li>
              </ul>
            </div>
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Language & Localization</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Preferred Language</label>
                  <select
                    value={preferences.language}
                    onChange={(e) => setPreferences({...preferences, language: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trust-500"
                  >
                    {languages.map(lang => (
                      <option key={lang.code} value={lang.code}>{lang.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Notification Preferences</h3>
              <div className="space-y-4">
                {Object.entries(preferences.notifications).map(([type, enabled]) => (
                  <div key={type} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-slate-800 capitalize">{type} Notifications</h4>
                      <p className="text-slate-600 text-sm">
                        Receive {type} notifications for bookings and messages
                      </p>
                    </div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => setPreferences({
                          ...preferences,
                          notifications: { ...preferences.notifications, [type]: e.target.checked }
                        })}
                        className="rounded"
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Privacy Settings</h3>
              <div className="space-y-4">
                {Object.entries(preferences.privacy).map(([setting, enabled]) => (
                  <div key={setting} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-slate-800">
                        {setting === 'showPhone' ? 'Show Phone Number' :
                         setting === 'showLocation' ? 'Show Location' :
                         'Allow Reviews'}
                      </h4>
                      <p className="text-slate-600 text-sm">
                        {setting === 'showPhone' ? 'Display your phone number on your profile' :
                         setting === 'showLocation' ? 'Show your location to potential clients' :
                         'Allow clients to leave reviews and ratings'}
                      </p>
                    </div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => setPreferences({
                          ...preferences,
                          privacy: { ...preferences.privacy, [setting]: e.target.checked }
                        })}
                        className="rounded"
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'data':
        return (
          <div className="space-y-6">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Data Export</h3>
              <p className="text-slate-600 mb-6">
                Export your data in compliance with GDPR regulations. 
                You can download all your information including profile data, bookings, and messages.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-800">Profile & Contact Information</h4>
                    <p className="text-slate-600 text-sm">Your basic profile data and contact details</p>
                  </div>
                  <motion.button
                    className="bg-trust-500 text-white px-4 py-2 rounded-lg hover:bg-trust-600 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiDownload} className="mr-2" />
                    Export
                  </motion.button>
                </div>

                <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-800">Booking History</h4>
                    <p className="text-slate-600 text-sm">All your past and upcoming bookings</p>
                  </div>
                  <motion.button
                    className="bg-trust-500 text-white px-4 py-2 rounded-lg hover:bg-trust-600 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiDownload} className="mr-2" />
                    Export
                  </motion.button>
                </div>

                <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-800">Messages & Communications</h4>
                    <p className="text-slate-600 text-sm">All messages and communication history</p>
                  </div>
                  <motion.button
                    className="bg-trust-500 text-white px-4 py-2 rounded-lg hover:bg-trust-600 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiDownload} className="mr-2" />
                    Export
                  </motion.button>
                </div>

                <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-800">Complete Data Package</h4>
                    <p className="text-slate-600 text-sm">All your data in a single download</p>
                  </div>
                  <motion.button
                    className="bg-olive-500 text-white px-4 py-2 rounded-lg hover:bg-olive-600 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiDownload} className="mr-2" />
                    Export All
                  </motion.button>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-2">Data Deletion</h4>
              <p className="text-red-700 text-sm mb-3">
                If you want to permanently delete your account and all associated data, 
                please contact our support team. This action cannot be undone.
              </p>
              <motion.button
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiLock} className="mr-2" />
                Request Account Deletion
              </motion.button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <motion.div 
        className="flex items-center justify-between"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Settings</h1>
          <p className="text-slate-600 mt-1">Manage your profile, preferences, and portal access</p>
        </div>
        
        <motion.button
          className="flex items-center gap-2 bg-trust-500 text-white px-6 py-3 rounded-lg hover:bg-trust-600 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <SafeIcon icon={FiSave} />
          Save Changes
        </motion.button>
      </motion.div>

      {/* Settings Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings Navigation */}
        <motion.div 
          className="lg:col-span-1"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h2 className="font-semibold text-slate-800">Settings</h2>
            </div>
            <div className="p-2">
              {tabs.map((tab) => (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                    activeTab === tab.id 
                      ? 'bg-trust-50 text-trust-600 border border-trust-200' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-trust-600'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={tab.icon} className="text-lg" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{tab.label}</div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Settings Content */}
        <motion.div 
          className="lg:col-span-3"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            {renderTabContent()}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Settings;